import { put, select, call } from 'redux-saga/effects';

import * as actions from '../../actions';
import generateSurveyLists from '../../../helpers/generateSurveyLists/generateSurveyLists';
import clearNotAllowed from './cleanSurveyStructure';

const ObjectID = require('bson-objectid');

const getFlows = (content, flows, group, nestingLevel = 0) => {
  const groupsList = content.groups;

  return flows.reduce((dict, el) => {
    if (groupsList && groupsList.some(g => g.id === el)) {
      const g = groupsList.find(gL => gL.id === el); // Check if el is a group

      if (nestingLevel === 1) {
        const parentGroup = groupsList.find(pG => pG.flows.indexOf(el) > -1);
        if (parentGroup) {
          g.parentGroup = JSON.parse(
            JSON.stringify({ ...parentGroup, parent: true, nestingLevel: 1 })
          );
        }
      }

      return [...dict, ...getFlows(content, g.flows, g, nestingLevel + 1)];
    }

    return [...dict, el];
  }, []);
};

const getSurveyQuestionIdsList = c => {
  if (!c || Object.values(c).length === 0) return [];

  const content = JSON.parse(JSON.stringify(c));

  return getFlows(content, content.flows);
};

const setTextInRichTextFormat = text => {
  const data = {
    blocks: [
      {
        key: '45g71',
        text, // JavaScript safely handles the '\' symbol here
        type: 'unstyled',
        depth: 0,
        inlineStyleRanges: [],
        entityRanges: [],
        data: {}
      }
    ],
    entityMap: {}
  };

  return JSON.stringify(data);
};

const filterObjectProperties = (obj, allowedKeys) =>
  Object.fromEntries(
    Object.entries(obj).filter(([key]) => allowedKeys.includes(key))
  );

const setNonExistingQuestionType = question => {
  if (
    question.choices &&
    question.choices.length > 0 &&
    question.matrix &&
    question.matrix.decisionFrameworkType &&
    question.matrix.xQuestionId
  ) {
    return 'matrix';
  }

  if (question.choices && question.choices.length > 0) {
    return 'mc_question';
  }

  if (question.branches && question.branches.length > 0 && !question.maxValue) {
    return 'distributor';
  }

  if (question.branches && question.branches.length > 0 && question.maxValue) {
    return 'slider';
  }

  if (
    (question.minCharacters || question.minCharacters === 0) &&
    question.maxCharacters
  ) {
    return 'open_question';
  }

  if (question.source) {
    return 'iframe';
  }

  return 'message';
};

const fixMissingFlowIds = (content, questions) => {
  // Helper to locate exactly which array contains a given ID, and at what index
  const findIdLocation = targetId => {
    if (!targetId) return null;

    // Check the main flows array first
    let index = content.flows.indexOf(targetId);
    if (index !== -1) {
      return { array: content.flows, index };
    }

    // Check inside each group's flows array
    if (content.groups && Array.isArray(content.groups)) {
      // eslint-disable-next-line no-restricted-syntax
      for (const group of content.groups) {
        index = group.flows.indexOf(targetId);
        if (index !== -1) {
          return { array: group.flows, index };
        }
      }
    }

    // Target ID not found anywhere
    return null;
  };

  let previousId = null;

  // Iterate through all parsed questions
  // eslint-disable-next-line no-restricted-syntax
  for (const question of questions) {
    const currentId = question.id;
    const currentLocation = findIdLocation(currentId);

    if (currentLocation) {
      // The ID exists in the flows. Update previousId and move on.
      previousId = currentId;
    } else {
      // The ID is missing. We need to insert it.
      if (previousId) {
        const prevLocation = findIdLocation(previousId);
        if (prevLocation) {
          // Splice the missing ID into the exact array (main or group) immediately after the previous ID
          prevLocation.array.splice(prevLocation.index + 1, 0, currentId);
        } else {
          // Fallback just in case (should not be reached if previousId is tracked correctly)
          content.flows.push(currentId);
        }
      } else {
        // Edge case: If the very first question in the array was missing
        content.flows.unshift(currentId);
      }

      // Update previousId so consecutive missing items are placed sequentially
      previousId = currentId;
    }
  }

  return content;
};

const processIds = (content, questions) => {
  const idsMapping = {};

  const allowedGroupProperties = [
    'id',
    'flows',
    'label',
    'nextFlow',
    'followUp',
    'randomized'
  ];

  const allowedQuestionProperties = [
    'id',
    'type',
    'question',
    'nextFlow',
    'end',
    'randomOrder',
    'rankAnswers',
    'allowOther',
    'selectAtLeast',
    'selectAtMost',
    'choices',
    'branches',
    'distributeEvenly',
    'profiling',
    'matrix',
    'source',
    'confirmResultWithApi',
    'minCharacters',
    'maxCharacters',
    'minValue',
    'maxValue',
    'forceNumberEnter'
  ];

  const allowedChoiceProperties = [
    'id',
    'answer',
    'nextFlow',
    'end',
    'profilingTag'
  ];

  const allowedBranchProperties = [
    'id',
    'answer',
    'nextFlow',
    'end',
    'quotaAmount',
    'quotaType',
    'range'
  ];

  const surveyQuestionIdsList = getSurveyQuestionIdsList(content);

  const questionsMapping = questions.reduce(
    (acc, q) => ({
      ...acc,
      [q.id.toString()]: q
    }),
    {}
  );

  // Questions
  const newQuestions = surveyQuestionIdsList.map(questionId => {
    const dbQuestion = questionsMapping[questionId];
    let newQuestion = { ...dbQuestion };
    newQuestion = filterObjectProperties(
      newQuestion,
      allowedQuestionProperties
    );

    // Check if question type is missing
    if (!newQuestion.type) {
      newQuestion.type = setNonExistingQuestionType(newQuestion);
    }

    const questionPositionIndex = surveyQuestionIdsList.indexOf(newQuestion.id);
    const nextQuestionId =
      questionPositionIndex > -1 &&
      surveyQuestionIdsList[questionPositionIndex + 1];

    const newQuestionId = ObjectID().toString();
    idsMapping[questionId.toString()] = newQuestionId;
    newQuestion.id = newQuestionId;
    newQuestion.question = newQuestion.question
      ? setTextInRichTextFormat(newQuestion.question)
      : null;

    if (newQuestion.choices) {
      const removeNextFlowFromChoices =
        newQuestion &&
        !newQuestion.nextFlow &&
        newQuestion.choices.every(c => c.nextFlow === nextQuestionId);

      newQuestion.choices = newQuestion.choices.map(c => {
        let newC = { ...c };

        if (removeNextFlowFromChoices) {
          delete newC.nextFlow;
        }

        newC = filterObjectProperties(newC, allowedChoiceProperties);

        const newId = ObjectID().toString();

        if (c.id) {
          idsMapping[c.id.toString()] = newId;
        }

        if (
          newQuestion.profiling &&
          newQuestion.profiling.isProfilingQuestion &&
          newC &&
          !newC.profilingTag
        ) {
          newC.profilingTag = {
            category: null,
            tagName: null,
            tagValue: null,
            excludeDescription: null
          };
        }

        return {
          ...newC,
          id: newId,
          answer: c.answer ? setTextInRichTextFormat(c.answer) : null
        };
      });
    }

    if (newQuestion.branches) {
      newQuestion.branches = newQuestion.branches.map(b => {
        let newB = { ...b };
        newB = filterObjectProperties(newB, allowedBranchProperties);

        const newId = ObjectID().toString();

        if (b.id) {
          idsMapping[b.id.toString()] = newId;
        }

        return {
          ...newB,
          id: newId
        };
      });
    }

    if (newQuestion.matrix && newQuestion.matrix.decisionFrameworkType) {
      newQuestion.matrix.xQuestionId = ObjectID().toString();
      newQuestion.matrix.yQuestionId = ObjectID().toString();
      newQuestion.matrix.xQuestion = newQuestion.matrix.xQuestion
        ? setTextInRichTextFormat(newQuestion.matrix.xQuestion)
        : null;
      newQuestion.matrix.yQuestion = newQuestion.matrix.yQuestion
        ? setTextInRichTextFormat(newQuestion.matrix.yQuestion)
        : null;

      if (newQuestion.matrix && newQuestion.matrix.decisionFrameworkType) {
        newQuestion.choices = newQuestion.choices.map(c => ({
          ...c,
          decisionFrameworkType: newQuestion.matrix.decisionFrameworkType
        }));
      }

      if (newQuestion.selectAtMost && newQuestion.selectAtMost !== 4) {
        newQuestion.selectAtMost = 4;
      }
    }

    if (
      !newQuestion.profiling ||
      (newQuestion.profiling && !newQuestion.profiling.id)
    ) {
      newQuestion.profiling = {
        isProfilingQuestion: false,
        isReusedProfilingQuestion: false,
        visibleForSearch: true,
        language: null,
        id: null,
        answerCollected: false
      };
    }

    if (newQuestion.profiling && newQuestion.profiling.id) {
      newQuestion.profiling.id =
        idsMapping[newQuestion.profiling.id.toString()] ||
        ObjectID().toString();
    }

    return newQuestion;
  });

  const newContent = {
    flows: [],
    groups: []
  };

  // Groups
  if (content && content.groups && content.groups.length > 0) {
    newContent.groups = content.groups.map(group => {
      const newGroup = filterObjectProperties(group, allowedGroupProperties);

      const newId = ObjectID().toString();
      idsMapping[newGroup.id.toString()] = newId;

      if (newGroup.nextFlow) {
        newGroup.nextFlow = idsMapping[newGroup.nextFlow];
      }

      if (newGroup.followUp) {
        newGroup.followUp = idsMapping[newGroup.followUp];
      }

      return {
        ...newGroup,
        id: newId
      };
    });
  }

  const questionsToInsert = newQuestions.map(question => {
    if (question.nextFlow) {
      // eslint-disable-next-line no-param-reassign
      question.nextFlow = idsMapping[question.nextFlow];
    }

    if (question.choices && question.choices.length > 0) {
      // eslint-disable-next-line no-param-reassign
      question.choices = question.choices.map(c => {
        if (!c.nextFlow) return c;

        // eslint-disable-next-line no-param-reassign
        c.nextFlow = idsMapping[c.nextFlow];
        return c;
      });
    }

    if (question.branches && question.branches.length > 0) {
      // eslint-disable-next-line no-param-reassign
      question.branches = question.branches.map(b => {
        if (!b.nextFlow) return b;

        // eslint-disable-next-line no-param-reassign
        b.nextFlow = idsMapping[b.nextFlow];
        return b;
      });
    }

    return question;
  });

  newContent.flows = content.flows.map(flowId => idsMapping[flowId.toString()]);
  newContent.groups = newContent.groups.map(group => ({
    ...group,
    flows: group.flows.map(flowId => idsMapping[flowId.toString()])
  }));

  if (!newContent.groups) {
    newContent.groups = [];
  }

  // Clean full survey structure
  const preGeneratedQuestionsMapping = questionsToInsert.reduce(
    (acc, q) => ({
      ...acc,
      [q.id]: q
    }),
    {}
  );
  const preGeneratedSurveyList = generateSurveyLists(
    newContent,
    preGeneratedQuestionsMapping
  );
  const {
    content: cleanedContent,
    questions: cleanedQuestionsToInsert
  } = clearNotAllowed(preGeneratedSurveyList, {
    content: newContent,
    questions: questionsToInsert
  });

  return {
    newContent: cleanedContent,
    questionsToInsert: cleanedQuestionsToInsert
  };
};

export default function*(action) {
  const surveyState = yield select(state => state.surveys);
  const authState = yield select(state => state.auth);

  yield put({ type: actions.SET_CUSTOM_LOADING, customLoading: true });

  const checkedContent = fixMissingFlowIds(
    { ...action.output.content },
    action.output.questions
  );

  const { newContent, questionsToInsert } = processIds(
    checkedContent,
    action.output.questions
  );

  const questionsMapping = questionsToInsert.reduce(
    (acc, q) => ({
      ...acc,
      [q.id]: q
    }),
    {}
  );

  const surveyLists = generateSurveyLists(newContent, questionsMapping);

  const newState = {
    ...surveyState,
    survey: {
      ...surveyState.survey,
      content: newContent,
      questions: questionsMapping
    },
    surveyLists
  };

  // Add new questions and save survey content
  // Set 0 waiting time to make sure loading is shown because of multiple api invocation in this sagas
  yield call(
    action.api,
    'addQuestions',
    {
      questionsArray: questionsToInsert,
      surveyContent: newContent
    },
    undefined,
    undefined,
    authState
  );

  yield put({ type: actions.SET_NEW_STATE, newState });
}
